import { useMutation, useQuery } from "@apollo/client";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { GridColDef, GridColumnGroupingModel } from "@mui/x-data-grid";
import { useEffect, useMemo, useState } from "react";
import { useNotify } from "react-admin";
import { CLIENT_ALLOCATIONS, CLIENT_CAPITAL_FLOWS, CLIENT_SWITCHES, SAVE_CLIENT_CASH_FLOW, SAVE_CLIENT_SWITCH } from "./graphql";
import {
  CapitalFlowReportRow,
  capitalFlowReport,
  emptyFields,
  FLOW_KEYS,
  flowCell,
  formatMoney,
  formatMonth,
  monthArg,
  parseNumber,
  toText,
} from "./logic";
import {
  ClientPicker,
  columnGroups,
  EmptyState,
  FilterBar,
  FundPicker,
  MonthField,
  Page,
  QueryState,
  ReportGrid,
  Section,
  ShareClassPicker,
  useSelectedClient,
  useSelectedFund,
  useSelectedMonth,
  useSelectedShareClass,
  useShareClasses,
} from "./shared";

const FLOW_LABEL = { addition: "Addition", redemption: "Redemption", switchIn: "Switch In", switchOut: "Switch Out" } as const;

export function CapitalFlowEntry() {
  const notify = useNotify();
  const [fundId, setFundId] = useSelectedFund();
  const [clientId, setClientId] = useSelectedClient();
  const [month, setMonth] = useSelectedMonth();
  const [shareClassId, setShareClassId] = useSelectedShareClass();
  const [toId, setToId] = useState<number | null>(null);
  const [addition, setAddition] = useState("");
  const [redemption, setRedemption] = useState("");
  const [switchAmount, setSwitchAmount] = useState("");

  const { shareClasses, loading, error } = useShareClasses(fundId, clientId);
  const both = { rcgFundId: fundId ?? 0, clientId: clientId ?? 0 };
  const skip = fundId === null || clientId === null;
  const flows = useQuery(CLIENT_CAPITAL_FLOWS, { variables: both, skip });
  const switches = useQuery(CLIENT_SWITCHES, { variables: both, skip });
  const allocations = useQuery(CLIENT_ALLOCATIONS, {
    variables: { rcgFundIds: fundId === null ? [] : [fundId], month: monthArg(month) },
    skip: fundId === null,
  });
  const [saveCashFlow, savingCashFlow] = useMutation(SAVE_CLIENT_CASH_FLOW);
  const [saveSwitch, savingSwitch] = useMutation(SAVE_CLIENT_SWITCH);

  const date = monthArg(month);
  const selected = shareClasses.find(s => s.shareClassId === shareClassId) ?? null;
  const to = shareClasses.find(s => s.shareClassId === toId) ?? null;
  const adjBegBal = (id: number | null) =>
    allocations.data?.clientAllocations.find(a => a.shareClassId === id)?.adjBegBal ?? 0;

  // Prefill from what is saved for the share class and month.
  useEffect(() => {
    const flow = flows.data?.clientCapitalFlows.find(f => f.shareClassId === shareClassId && f.date === date);
    setAddition(toText(flow?.addition));
    setRedemption(toText(flow?.redemption === null || flow?.redemption === undefined ? null : -flow.redemption));
  }, [flows.data, shareClassId, date]);

  // Switch target defaults to a different share class.
  useEffect(() => {
    if (toId === null || toId === shareClassId || !to) {
      setToId(shareClasses.find(s => s.shareClassId !== shareClassId)?.shareClassId ?? null);
    }
  }, [shareClasses, shareClassId, toId, to]);

  useEffect(() => {
    const sw = switches.data?.clientSwitches.find(
      s => s.fromShareClassId === shareClassId && s.toShareClassId === toId && s.date === date
    );
    setSwitchAmount(toText(sw?.amount));
  }, [switches.data, shareClassId, toId, date]);

  const refetch = () => Promise.all([flows.refetch(), switches.refetch(), allocations.refetch()]);

  const run = async (work: () => Promise<unknown>, done: string) => {
    try {
      await work();
      await refetch();
      notify(done, { type: "success" });
    } catch (e) {
      notify(e instanceof Error ? e.message : String(e), { type: "error" });
    }
  };

  const onSaveCashFlow = () =>
    run(async () => {
      if (shareClassId === null) throw new Error("Pick a share class");
      // ponytail: sign and adjBegBal limits are the server's checks; its message is shown.
      const a = parseNumber(addition, "Addition");
      const r = parseNumber(redemption, "Redemption");
      await saveCashFlow({ variables: { shareClassId, month: date, addition: a, redemption: r } });
    }, "Cash flow saved");

  const onSaveSwitch = () =>
    run(async () => {
      if (shareClassId === null || toId === null) throw new Error("Pick both share classes");
      if (shareClassId === toId) throw new Error("A switch must be between two different share classes");
      const amount = parseNumber(switchAmount, "Switch amount") ?? 0;
      await saveSwitch({ variables: { fromShareClassId: shareClassId, toShareClassId: toId, month: date, amount } });
    }, amountIsZero(switchAmount) ? "Switch deleted" : "Switch saved");

  const report = useMemo(() => capitalFlowReport(flows.data?.clientCapitalFlows ?? []), [flows.data]);
  const { columns, groups } = useMemo(() => {
    const ids = new Set((flows.data?.clientCapitalFlows ?? []).map(f => f.shareClassId));
    const reported = shareClasses.filter(s => ids.has(s.shareClassId));
    // Only the flows a share class ever had: a column of blanks per class and flow is most of the grid.
    const cells = reported.flatMap(s => FLOW_KEYS.map(k => flowCell(s.shareClassId, k)));
    const empty = emptyFields(report.map(r => r.values), cells);
    const flowCols = reported.flatMap(s =>
      FLOW_KEYS.filter(k => !empty.has(flowCell(s.shareClassId, k))).map(
        (k): GridColDef<CapitalFlowReportRow> => ({
          field: flowCell(s.shareClassId, k),
          headerName: FLOW_LABEL[k],
          type: "number",
          width: 130,
          valueGetter: (_v, row) => row.values[flowCell(s.shareClassId, k)] ?? null,
          valueFormatter: (v: number | null) => formatMoney(v),
        })
      )
    );
    const cols: GridColDef<CapitalFlowReportRow>[] = [
      { field: "date", headerName: "Month", width: 110, valueGetter: (_v, row) => (row.date === null ? "Total" : formatMonth(row.date)) },
      ...flowCols,
    ];
    const model: GridColumnGroupingModel = columnGroups(
      cols,
      reported.map(s => ({
        groupId: String(s.shareClassId),
        headerName: s.derived ? `${s.name} (derived)` : s.name,
        fields: FLOW_KEYS.map(k => flowCell(s.shareClassId, k)),
      }))
    );
    return { columns: cols, groups: model };
  }, [flows.data, shareClasses, report]);

  const enterable = selected !== null && !selected.derived;
  const switchEnterable = enterable && to !== null && !to.derived;
  const amountProps = { inputMode: "decimal", style: { textAlign: "right" } } as const;
  const monthLabel = formatMonth(date);

  return (
    <Page title="Capital Flow Entry" description="Additions, redemptions and switches for one client's share classes in a fund, one month at a time.">
      <FilterBar>
        <FundPicker
          value={fundId}
          onChange={id => {
            setFundId(id);
            setShareClassId(null);
          }}
        />
        <ClientPicker
          rcgFundId={fundId}
          value={clientId}
          onChange={id => {
            setClientId(id);
            setShareClassId(null);
          }}
        />
        <MonthField value={month} onChange={setMonth} />
      </FilterBar>
      <QueryState
        loading={loading || flows.loading || switches.loading || allocations.loading}
        error={error ?? flows.error ?? switches.error ?? allocations.error}
      />
      {skip ? (
        <EmptyState>Pick a fund and a client to enter their capital flows.</EmptyState>
      ) : (
        <>
          <Section title={`Addition / Redemption, ${monthLabel}`}>
            <Stack spacing={2}>
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                <ShareClassPicker shareClasses={shareClasses} value={shareClassId} onChange={setShareClassId} />
                {selected && (
                  <Typography variant="body2" color="text.secondary">
                    Adj. beg. balance: {formatMoney(adjBegBal(shareClassId))}
                  </Typography>
                )}
              </Stack>
              {selected === null && (
                <Typography variant="body2" color="text.secondary">
                  {shareClasses.length ? "Pick a share class to enter its flows." : "This client has no share classes in this fund yet (add one on Share Class Entry)."}
                </Typography>
              )}
              {selected?.derived && <Alert severity="info">This share class is derived; its flows can't be entered.</Alert>}
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                <TextField size="small" sx={{ width: 200 }} inputProps={amountProps} label="Addition" value={addition} onChange={e => setAddition(e.target.value)} disabled={!enterable} />
                <TextField size="small" sx={{ width: 200 }} inputProps={amountProps} label="Redemption" value={redemption} onChange={e => setRedemption(e.target.value)} disabled={!enterable} />
                <Button disabled={!enterable} onClick={() => setRedemption(toText(adjBegBal(shareClassId)))}>
                  Fully redeem
                </Button>
                <Button variant="contained" disabled={!enterable || savingCashFlow.loading} onClick={onSaveCashFlow}>
                  Save cash flow
                </Button>
              </Stack>
            </Stack>
          </Section>
          <Section title={`Switch, ${monthLabel}`}>
            <Stack spacing={2}>
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                <ShareClassPicker label="From" shareClasses={shareClasses} value={shareClassId} onChange={setShareClassId} />
                <ShareClassPicker
                  label="To"
                  shareClasses={shareClasses.filter(s => s.shareClassId !== shareClassId)}
                  value={toId}
                  onChange={setToId}
                />
                <TextField size="small" sx={{ width: 200 }} inputProps={amountProps} label="Amount" value={switchAmount} onChange={e => setSwitchAmount(e.target.value)} disabled={!switchEnterable} />
                <Button disabled={!switchEnterable} onClick={() => setSwitchAmount(toText(adjBegBal(shareClassId)))}>
                  Fully switch
                </Button>
                <Button variant="contained" disabled={!switchEnterable || savingSwitch.loading} onClick={onSaveSwitch}>
                  Save switch
                </Button>
              </Stack>
              {to?.derived && <Alert severity="info">{to.name} is derived; it can't take a switch.</Alert>}
              <Typography variant="caption" color="text.secondary">
                A switch needs two share classes that aren't derived. An amount of 0 deletes the switch.
              </Typography>
            </Stack>
          </Section>
          <Section title="Capital Flow Report">
            <ReportGrid<CapitalFlowReportRow>
              rows={report}
              columns={columns}
              columnGroupingModel={groups}
              noRowsLabel="No capital flows for this client in this fund"
            />
          </Section>
        </>
      )}
    </Page>
  );
}

const amountIsZero = (text: string) => {
  const t = text.trim();
  return t === "" || Number(t) === 0;
};
